/**
 * Every font bundled with the desktop app and the browser display (both
 * import the matching `@fontsource` CSS in their own `globals.css` — keep
 * the two lists in step with this one). OFL-licensed only (PRD FR-TYP-9).
 */
export const THEME_FONTS = [
    {
        name: "Inter",
        category: "sans",
        stack: '"Inter Variable", "Inter", system-ui, sans-serif',
        scale: 1,
    },
    { name: "Roboto", category: "sans", stack: '"Roboto", system-ui, sans-serif', scale: 1 },
    {
        name: "Montserrat",
        category: "sans",
        stack: '"Montserrat Variable", "Montserrat", system-ui, sans-serif',
        scale: 0.94,
    },
    { name: "Poppins", category: "sans", stack: '"Poppins", system-ui, sans-serif', scale: 0.95 },
    {
        name: "Merriweather",
        category: "serif",
        stack: '"Merriweather Variable", "Merriweather", Georgia, serif',
        scale: 0.92,
    },
    { name: "Barlow", category: "sans", stack: '"Barlow", system-ui, sans-serif', scale: 1 },
    {
        name: "Barlow Condensed",
        category: "condensed",
        stack: '"Barlow Condensed", "Barlow", system-ui, sans-serif',
        scale: 1,
    },
    {
        name: "Oswald",
        category: "condensed",
        stack: '"Oswald Variable", "Oswald", "Barlow Condensed", system-ui, sans-serif',
        scale: 0.85,
    },
    {
        name: "Teko",
        category: "condensed",
        stack: '"Teko Variable", "Teko", "Oswald Variable", system-ui, sans-serif',
        scale: 0.8,
    },
    {
        name: "Bebas Neue",
        category: "display",
        stack: '"Bebas Neue", "Oswald Variable", sans-serif',
        scale: 0.92,
    },
    {
        name: "Orbitron",
        category: "display",
        stack: '"Orbitron Variable", "Orbitron", "Roboto Mono", monospace',
        scale: 0.85,
    },
    {
        name: "Roboto Mono",
        category: "mono",
        stack: '"Roboto Mono", ui-monospace, SFMono-Regular, Consolas, monospace',
        scale: 1,
    },
    {
        name: "Share Tech Mono",
        category: "mono",
        stack: '"Share Tech Mono", "Roboto Mono", ui-monospace, Consolas, monospace',
        scale: 1,
    },
];
/** The theme-wide size multiplier token — `"1"` is 100%. Absent or unparsable means 100%. */
export const FONT_SIZE_TOKEN = "font.size";
const MIN_FONT_SIZE = 0.5;
const MAX_FONT_SIZE = 1.5;
/** What the theme card's "Size" picker offers. */
export const THEME_FONT_SIZE_OPTIONS = [0.7, 0.8, 0.9, 1, 1.1, 1.2, 1.3].map((value) => ({
    label: `${Math.round(value * 100)}%`,
    value,
}));
export function findThemeFont(name) {
    const trimmed = name?.trim().toLowerCase();
    if (!trimmed)
        return undefined;
    return THEME_FONTS.find((font) => font.name.toLowerCase() === trimmed);
}
/** The theme's own `font.size` token, clamped to a sane range; `1` when absent or invalid. */
export function readFontSizeToken(tokens) {
    const parsed = Number.parseFloat(tokens[FONT_SIZE_TOKEN] ?? "");
    if (!Number.isFinite(parsed))
        return 1;
    return Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, parsed));
}
/** Total multiplier for text set in `fontName`: the face's own correction times the theme's size token. */
export function resolveFontScale(fontName, tokens) {
    return (findThemeFont(fontName)?.scale ?? 1) * readFontSizeToken(tokens);
}
/** Wraps a CSS size (`"4vh"`, `"clamp(...)"`) so it draws `scale` times as big; untouched at exactly 1. */
export function scaleFontSize(size, scale) {
    return scale === 1 ? size : `calc(${size} * ${Number(scale.toFixed(3))})`;
}
export const DEFAULT_THEME_FONT = "Inter";
/** PRD FR-TYP-8: a font that isn't bundled or installed falls back to bundled Inter, never blank/tofu. */
const FALLBACK_STACK = '"Inter Variable", "Inter", system-ui, sans-serif';
/**
 * Turns a theme's `fontFamily` value (a bare family name from the
 * `font.family` token, or an author-written stack from an imported theme)
 * into a CSS `font-family` value that always ends in the bundled fallback.
 */
export function resolveFontStack(value) {
    const trimmed = value?.trim();
    if (!trimmed)
        return undefined;
    const bundled = findThemeFont(trimmed);
    if (bundled)
        return bundled.stack;
    if (trimmed.includes(","))
        return `${trimmed}, ${FALLBACK_STACK}`;
    const bare = trimmed.replace(/^["']|["']$/g, "").replace(/"/g, "");
    return `"${bare}", ${FALLBACK_STACK}`;
}
//# sourceMappingURL=fonts.js.map